import { connect } from 'react-redux';

import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';

import {
    setNextPreviewsInfo,
    setTrack,
} from '../redux/actions/dataActions';

import { getAnalytics, logEvent } from "firebase/analytics";
import axios from 'axios';
import { loadTrack } from '../components/audioHelper';

import { PauseRounded, PlayArrowRounded } from '@mui/icons-material';

import IconSmall from '../components/IconSmall';
import PrimaryActionButton from '../components/PrimaryActionButton';
import PreviewShareButton from '../components/PreviewShareButton';
import { fancyTimeFormatWithHM } from '../components/timeHelper';

import PreviewInfo from '../components/PreviewInfo';
import PreviewInfosLoading from '../components/PreviewInfosLoading';
import { EXPLORE_PREVIEW_INFO_PROPS } from '../constants/preview_info_constants';
import { buildMissingRoute } from '../utils/missingRoute';
import Previews from './previews';
import { getOnboardingPreviewPath, readOnboardingSession } from '../utils/onboardingSession';
import { getPreviewViewerSession } from '../utils/previewViewerSessions';
import './preview.css';


export const Preview = ({
    authenticated,
    setNextPreviewsInfo,
    setTrack,
}) => {
    const location = useLocation();
    const navigate = useNavigate();
    const { trackId, messageIndex } = useParams();
    const analytics = getAnalytics();
    const missingRoute = buildMissingRoute(location);

    const [previewInfo, setPreviewInfo] = useState(null);
    const [preview, setPreview] = useState(null);
    const [playPreviewTapped, setPlayPreviewTapped] = useState(false);
    const [playing, setPlaying] = useState(false);

    useEffect(() => {
        logEvent(analytics, 'screen_view', {
            firebase_screen: 'preview'
        });
    }, [analytics]);

    const tapPlayPreview = () => {
        if (!playPreviewTapped && previewInfo?.mp3URL) {
            loadTrack(previewInfo.mp3URL, 0);
            setPlayPreviewTapped(true);
        }
    }

    useEffect(() => {
        let cancelled = false;
        setPreviewInfo(null);
        setPreview(null);
        setPlayPreviewTapped(false);
        setPlaying(false);

        const handleLoadError = (error) => {
            if (cancelled) return;
            console.log(error);
            navigate(missingRoute);
        };

        if (trackId && messageIndex) {
            axios
                .get(`/preview_info/${trackId}/${messageIndex}`)
                .then((res) => {
                    if (cancelled) return;
                    setPreviewInfo(res.data);
                })
                .catch(handleLoadError);

            axios
                .get(`/preview/${trackId}/${messageIndex}`)
                .then((res) => {
                    if (cancelled) return;
                    setPreview(res.data);
                })
                .catch(handleLoadError);
        }
        return () => { cancelled = true; };
    }, [messageIndex, missingRoute, navigate, trackId]);

    useEffect(() => {
        if (!playPreviewTapped || !preview || !previewInfo) return;
        setTrack(preview);
        setNextPreviewsInfo(authenticated, [previewInfo], { playbackOrigin: { kind: 'shared' } });
        navigate('/previews');
    }, [authenticated, navigate, playPreviewTapped, preview, previewInfo, setNextPreviewsInfo, setTrack]);

    useEffect(() => {
        const audio = document.getElementById('audio');

        if (!audio) {
            return undefined;
        }

        const onPause = () => {
            setPlaying(false);
        }

        const onPlay = () => {
            setPlaying(true);
        }
        audio.addEventListener('pause', onPause);
        audio.addEventListener('play', onPlay);

        return () => {
            audio.removeEventListener('pause', onPause);
            audio.removeEventListener('play', onPlay);
        }
    }, []);

    const playPauseClick = () => {
        const audio = document.getElementById('audio');
        if (!audio) {
            return;
        }
        if (playing) {
            audio.pause();
        } else {
            audio.play().catch(() => {
                // The transcript navigation can interrupt a pending play request.
            });
        }
    }

    const viewFullEpisode = () => {
        const episodeId = previewInfo.fullTrackId || trackId;
        const requestedStart = Number(previewInfo.fullTrackStartTimeSecs ?? preview?.previewStartTime ?? 0);
        const start = Number.isFinite(requestedStart) && requestedStart >= 0 ? requestedStart : 0;
        navigate(`/track/${encodeURIComponent(episodeId)}?autoplay=false&start=${start}`);
    };

    const duration = fancyTimeFormatWithHM(previewInfo?.totalTimeSeconds);

    return (
        <main className='container appScreen sharedHighlightPage'>
            <header className='sharedHighlightHeader'>
                <button type='button' className='sharedHighlightHome' aria-label='Home' onClick={() => navigate('/home')}>
                    <IconSmall interactive={false} />
                </button>
                <h1>Shared highlight</h1>
                {previewInfo ? <PreviewShareButton
                    className='sharedHighlightShare'
                    trackId={previewInfo.fullTrackId || trackId}
                    messageIndex={previewInfo.messageIndex ?? messageIndex}
                /> : <span aria-hidden='true' />}
            </header>
            {previewInfo ?
                <div className='sharedHighlightContent'>
                    <PreviewInfo
                        preview={previewInfo}
                        playPreview={tapPlayPreview}
                        index={0}
                        {...EXPLORE_PREVIEW_INFO_PROPS}
                        showFooterControls={false}
                    />

                    <div className='sharedHighlightActions'>
                        <PrimaryActionButton
                            className='sharedHighlightPlay'
                            onClick={tapPlayPreview}
                            disabled={!previewInfo.mp3URL || playPreviewTapped}
                        >
                            <PlayArrowRounded aria-hidden='true' />
                            <span>Play highlight{duration ? ` · ${duration}` : ''}</span>
                        </PrimaryActionButton>
                        <button type='button' className='sharedHighlightEpisode' onClick={viewFullEpisode}>
                            View full episode
                        </button>
                    </div>

                    {playPreviewTapped &&
                        <div className='previewPlaybackStatus'>
                            <button
                                onClick={playPauseClick}
                                className='previewPlaybackControl'
                                aria-label={playing ? 'Pause preview' : 'Play preview'}
                            >
                                {playing ?
                                    <PauseRounded style={{ fontSize: '24px' }} />
                                    :
                                    <PlayArrowRounded style={{ fontSize: '24px' }} />
                                }
                            </button>
                            <span className='previewPlaybackText' role='status'>
                                Loading transcript...
                            </span>
                        </div>
                    }
                </div>
                :
                <div className='previewPageLoading'>
                    <PreviewInfosLoading />
                </div>
            }
        </main>
    );
};

const mapStateToProps = (state) => ({
    authenticated: state.user.authenticated,
})

const mapActionsToProps = {
    setNextPreviewsInfo,
    setTrack,
}

const ConnectedPreview = connect(mapStateToProps, mapActionsToProps)(Preview);

// The transcript player replaces its URL with a shareable /preview path. A
// refresh of the onboarding sample should reopen that player, still paused.
export const PreviewRoute = props => {
    const location = useLocation();
    const { pathname } = location;
    const session = readOnboardingSession();
    if (session && getOnboardingPreviewPath(session) === pathname) return <Previews onboardingRecovery />;
    return getPreviewViewerSession(location) ? <Previews {...props} /> : <ConnectedPreview {...props} />;
};

export default PreviewRoute;
